import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Loader2, KeyRound } from "lucide-react";
import api from "../api/client";

/**
 * Self-serve forgot-password flow.
 * 1. User enters email → backend returns the two security question texts
 *    (empty list if no Q&A on file, in which case we tell them to contact
 *    their admin — no email enumeration).
 * 2. User answers both + types a new password → backend verifies and resets.
 * 3. An SSE event is published to admins ('password_reset_self_serve') after
 *    the fact so the team is notified.
 */
export default function ForgotPasswordModal({ open, onClose, initialEmail = "" }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(["", ""]);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const reset = useCallback(() => {
    setStep(1);
    setEmail(initialEmail);
    setQuestions([]);
    setAnswers(["", ""]);
    setNewPwd("");
    setConfirmPwd("");
    setErr("");
  }, [initialEmail]);

  const close = useCallback(() => { reset(); onClose(); }, [onClose, reset]);

  const startReset = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const cleaned = email.trim().toLowerCase();
      const { data } = await api.post("/auth/forgot-password/start", { email: cleaned });
      if (!data.questions || data.questions.length < 2) {
        setErr("No security questions on file for this email. Please contact your administrator to reset your password.");
        return;
      }
      setQuestions(data.questions);
      setAnswers(["", ""]);
      setStep(2);
    } catch {
      setErr("Could not look up that email. Please try again.");
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr("");
    const n = newPwd.trim();
    const c = confirmPwd.trim();
    if (n.length < 4) { setErr("New password must be at least 4 characters"); return; }
    if (n !== c) { setErr("Passwords do not match"); return; }
    if (!answers[0].trim() || !answers[1].trim()) { setErr("Please answer both questions"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify", {
        email: email.trim().toLowerCase(),
        answers: questions.map((q, i) => ({ question_id: q.question_id, answer: answers[i].trim() })),
        new_password: n,
      });
      toast.success("Password reset — you can sign in with your new password");
      close();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not verify your identity. Check your answers.");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4"
      onClick={close}
      data-testid="forgot-password-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-gray-300 max-w-md w-full p-6 rounded-sm shadow-2xl"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-[#002FA7] text-white p-2 rounded-sm"><KeyRound className="w-5 h-5" /></div>
          <div className="flex-1">
            <h4 className="font-black text-xl tracking-tight">Forgot password?</h4>
            <p className="text-xs text-gray-500 mt-1">
              {step === 1
                ? "Enter your email and we'll show your security questions."
                : "Answer both questions and choose a new password."}
            </p>
          </div>
          <button onClick={close}><X className="w-5 h-5" /></button>
        </div>

        {step === 1 && (
          <form onSubmit={startReset}>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Email</label>
            <input
              required
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="fp-email-input"
              className="w-full border border-gray-300 px-3 py-2 mb-4 rounded-sm text-sm"
            />
            {err && <div className="mb-3 border-l-4 border-red-600 bg-red-50 px-3 py-2 text-xs text-red-900" data-testid="fp-error">{err}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="fp-continue"
              className="w-full bg-[#002FA7] text-white py-2.5 font-bold rounded-sm hover:bg-[#002277] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              CONTINUE
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitReset}>
            {questions.map((q, i) => (
              <div key={q.question_id} className="mb-3">
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  {q.text}
                </label>
                <input
                  required
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={answers[i]}
                  onChange={(e) => setAnswers((p) => p.map((v, idx) => (idx === i ? e.target.value : v)))}
                  data-testid={`fp-answer-${i}`}
                  className="w-full border border-gray-300 px-3 py-2 rounded-sm text-sm"
                />
              </div>
            ))}

            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1 mt-4">New password</label>
            <input
              required
              type="text"
              autoComplete="new-password"
              autoCapitalize="off"
              spellCheck={false}
              minLength={4}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              data-testid="fp-new-password"
              className="w-full border border-gray-300 px-3 py-2 mb-3 rounded-sm text-sm font-mono"
            />
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Confirm new password</label>
            <input
              required
              type="text"
              autoComplete="new-password"
              autoCapitalize="off"
              spellCheck={false}
              minLength={4}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              data-testid="fp-confirm-password"
              className="w-full border border-gray-300 px-3 py-2 mb-4 rounded-sm text-sm font-mono"
            />

            {err && <div className="mb-3 border-l-4 border-red-600 bg-red-50 px-3 py-2 text-xs text-red-900" data-testid="fp-error">{err}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="fp-submit"
              className="w-full bg-[#002FA7] text-white py-2.5 font-bold rounded-sm hover:bg-[#002277] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              RESET PASSWORD
            </button>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full mt-3 text-xs text-gray-500 hover:text-gray-900"
              data-testid="fp-back"
            >
              ← Use a different email
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
